import React, { useRef, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress
} from '@mui/material';
import { updateJobDefinition } from 'graphql/mutations';
import { JobForm } from './JobForm';
import { useUser } from 'components/RDAuthContext';

// Constants for Phase 0 (to be replaced with Data Source Lambda in future phases)
const PHASE_0_CONSTANTS = {
  DATA_SOURCES: [
    { id: 'toast-pos', name: 'Toast POS' }
  ],
  SOURCE_ENDPOINTS: [
    { id: 'orders', name: 'Orders', dataSourceId: 'toast-pos' }
  ],
  // Define more constants as needed
};

export const EditJobModal = ({ job, onClose }) => {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useUser();

  console.log('EditJobModal - job:', job);

  // Parse settings if it's a string
  const parsedSettings = typeof job.settings === 'string' 
    ? JSON.parse(job.settings) 
    : job.settings || {};

  // Prepare default values for the form
  const defaultValues = {
    ...job,
    settings: parsedSettings,
  };

  const handleSubmit = async (formData) => {
    console.log('handleSubmit formData', formData);

    setLoading(true);
    setError(null);

    try {
      // Get current timestamp
      const now = new Date().toISOString();
      
      // Build dataTimeConfig based on selected mode
      let dataTimeConfig = {
        mode: formData.dataTimeConfig.mode
      };

      if (formData.dataTimeConfig.mode === 'RELATIVE') {
        dataTimeConfig.relativeValue = formData.dataTimeConfig.relativeValue;
        dataTimeConfig.relativeUnit = formData.dataTimeConfig.relativeUnit;
        if (formData.dataTimeConfig.granularity) {
          dataTimeConfig.granularity = formData.dataTimeConfig.granularity;
        }
      } else if (formData.dataTimeConfig.mode === 'ABSOLUTE') {
        // Ensure dates are in proper AWS DateTime format
        if (formData.dataTimeConfig.startDateTime) {
          try {
            // Parse the date and convert to full ISO string
            const startDate = new Date(formData.dataTimeConfig.startDateTime);
            if (isNaN(startDate.getTime())) {
              throw new Error('Invalid start date');
            }
            dataTimeConfig.startDateTime = startDate.toISOString();
          } catch (err) {
            console.error('Error formatting startDateTime:', err);
            setError('Invalid format for Start Date');
            setLoading(false);
            return;
          }
        }
        
        if (formData.dataTimeConfig.endDateTime) {
          try {
            // Parse the date and convert to full ISO string
            const endDate = new Date(formData.dataTimeConfig.endDateTime);
            if (isNaN(endDate.getTime())) {
              throw new Error('Invalid end date');
            }
            dataTimeConfig.endDateTime = endDate.toISOString();
          } catch (err) {
            console.error('Error formatting endDateTime:', err);
            setError('Invalid format for End Date');
            setLoading(false);
            return;
          }
        }
        
        if (formData.dataTimeConfig.granularity) {
          dataTimeConfig.granularity = formData.dataTimeConfig.granularity;
        }
      }
      
      // Handle schedule config
      let scheduleConfig = {
        mode: formData.scheduleConfig.mode,
        timeZoneStrategy: formData.scheduleConfig.timeZoneStrategy,
      };
      
      if (formData.scheduleConfig.mode === 'RECURRING') {
        scheduleConfig.cronExpression = formData.scheduleConfig.cronExpression;
      } else if (formData.scheduleConfig.mode === 'ONE_TIME') {
        // Ensure scheduledTime is in proper format
        if (formData.scheduleConfig.scheduledTime) {
          try {
            const scheduledDate = new Date(formData.scheduleConfig.scheduledTime);
            if (isNaN(scheduledDate.getTime())) {
              throw new Error('Invalid scheduled time');
            }
            scheduleConfig.scheduledTime = scheduledDate.toISOString();
          } catch (err) {
            console.error('Error formatting scheduledTime:', err);
            setError('Invalid format for Scheduled Time');
            setLoading(false);
            return;
          }
        }
      }
      
      // Handle time zone settings
      if (formData.scheduleConfig.timeZoneStrategy === 'SPECIFIC') {
        scheduleConfig.specificTimeZone = formData.scheduleConfig.specificTimeZone;
      }
      
      // Handle optional schedule window dates
      if (formData.scheduleConfig.scheduleStartDate) {
        try {
          const startDate = new Date(formData.scheduleConfig.scheduleStartDate);
          if (!isNaN(startDate.getTime())) {
            scheduleConfig.scheduleStartDate = startDate.toISOString();
          }
        } catch (err) {
          console.error('Error formatting scheduleStartDate:', err);
        }
      }
      
      if (formData.scheduleConfig.scheduleEndDate) {
        try {
          const endDate = new Date(formData.scheduleConfig.scheduleEndDate);
          if (!isNaN(endDate.getTime())) {
            scheduleConfig.scheduleEndDate = endDate.toISOString();
          }
        } catch (err) {
          console.error('Error formatting scheduleEndDate:', err);
        }
      }
      
      // Remove any extra fields from sourceFilters that might cause errors
      const cleanSourceFilters = formData.sourceFilters.map(filter => ({
        name: filter.name,
        values: filter.values
      }));
      
      // Prepare the minimal input for GraphQL mutation - only include fields we need to update
      const input = {
        jobDefinitionId: job.jobDefinitionId, // Required for update
        name: formData.name,
        description: formData.description || '',
        
        // Source configuration - cleaned to match expected input type
        sourceFilters: cleanSourceFilters,
        
        // Data Time Configuration
        dataTimeConfig,
        
        // Schedule Configuration
        scheduleConfig,
        
        // Status and metadata
        status: formData.status,
        lastModified: now,
        
        // Optional configuration
        settings: JSON.stringify(formData.settings || {}),
      };

      console.log('Submitting to GraphQL:', input);

      // Execute the mutation
      const result = await API.graphql(graphqlOperation(updateJobDefinition, { input }));

      console.log('GraphQL mutation result:', result);
      
      // Close modal and refresh the list
      onClose(true);
    } catch (err) {
      console.error('Error updating job definition:', err);
      setError(err.message || 'Failed to update job definition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={true} 
      onClose={() => !loading && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Job Definition</DialogTitle>
      
      <DialogContent>
        <JobForm 
          ref={formRef}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          error={error}
          loading={loading}
          isEdit={true}
          dataSources={PHASE_0_CONSTANTS.DATA_SOURCES}
          sourceEndpoints={PHASE_0_CONSTANTS.SOURCE_ENDPOINTS}
        />
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose()} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={() => formRef.current.submitForm()}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};